// Top toolbar: brand, map dropdown, tool-mode buttons, Add menu, Import menu,
// snap controls, axis lock, world/local space, playtest/export/import.

import { listBundles } from '../../maps/registry';
import type { EditorState } from './EditorState';
import type { EditorTool, ToolMode } from './EditorTool';
import { renderIcon, iconButton, type IconNode } from './icons';
import {
  Layers, Globe, Box, Plus, Upload, Download, Play, MousePointer2, Move,
  Rotate3d, Maximize2, Magnet, FilePlus2, Image as ImageIcon,
  Lightbulb, Cog, ChevronDown, Hand
} from 'lucide';

export interface ToolbarCallbacks {
  onMapChange(id: string): void;
  onAddPrimitive(kind: 'box' | 'sphere' | 'cylinder' | 'capsule' | 'plane' | 'circle' | 'group'): void;
  onAddLight(kind: 'point' | 'directional' | 'ambient'): void;
  onImportMesh(): void;
  onImportHdr(): void;
  onImportZip(): void;
  onExport(): void;
  onPlaytest(): void;
  onExit(): void;
}

export class Toolbar {
  readonly root: HTMLElement;
  private state: EditorState;
  private tool: EditorTool;
  private cbs: ToolbarCallbacks;
  private mapSelect!: HTMLSelectElement;
  private toolButtons = new Map<ToolMode, HTMLButtonElement>();
  private spaceBtn!: HTMLButtonElement;
  private snapBtn!: HTMLButtonElement;
  private snapInput!: HTMLInputElement;
  private axisBtns = new Map<'x' | 'y' | 'z', HTMLButtonElement>();
  private playtestBtn!: HTMLButtonElement;
  private unsubTool: () => void;
  private unsubDoc: () => void;

  constructor(parent: HTMLElement, state: EditorState, tool: EditorTool, cbs: ToolbarCallbacks) {
    this.state = state;
    this.tool = tool;
    this.cbs = cbs;
    this.root = document.createElement('div');
    this.root.className = 'ed-toolbar';
    parent.appendChild(this.root);
    this.build();
    this.unsubTool = this.tool.subscribe(() => this.applyToolState());
    this.unsubDoc = this.state.subscribeDoc(() => this.applyDocState());
    this.applyToolState();
    this.applyDocState();
  }

  dispose(): void {
    this.unsubTool();
    this.unsubDoc();
    this.root.remove();
  }

  private build(): void {
    // Brand + map select
    const brand = document.createElement('div');
    brand.className = 'ed-toolbar-group';
    const h1 = document.createElement('h1');
    h1.innerHTML = `<span class="ed-icon">${renderIcon(Layers, 16)}</span><span>MAP EDITOR</span>`;
    brand.appendChild(h1);
    this.mapSelect = document.createElement('select');
    this.mapSelect.className = 'ed-select';
    for (const b of listBundles()) {
      const opt = document.createElement('option');
      opt.value = b.id;
      const tag = b.json.hiddenFromPicker ? ' [dev]' : '';
      opt.textContent = `${b.json.label}${tag}`;
      this.mapSelect.appendChild(opt);
    }
    this.mapSelect.value = this.state.get().bundleId;
    this.mapSelect.onchange = () => this.cbs.onMapChange(this.mapSelect.value);
    brand.appendChild(this.mapSelect);
    this.root.appendChild(brand);

    // Tool modes
    const tools = document.createElement('div');
    tools.className = 'ed-toolbar-group';
    const mkTool = (mode: ToolMode, icon: IconNode, label: string, key: string) => {
      const b = iconButton({
        icon, title: `${label} (${key.toUpperCase()})`,
        onClick: () => this.tool.setMode(mode)
      });
      this.toolButtons.set(mode, b);
      tools.appendChild(b);
    };
    mkTool('control', Hand, 'Control / Fly', 't');
    mkTool('select', MousePointer2, 'Select', 'q');
    mkTool('translate', Move, 'Translate', 'w');
    mkTool('rotate', Rotate3d, 'Rotate', 'e');
    mkTool('scale', Maximize2, 'Scale', 'r');
    this.root.appendChild(tools);

    // Add / Import
    const adders = document.createElement('div');
    adders.className = 'ed-toolbar-group';
    adders.appendChild(this.popoverButton(Plus, 'Add', [
      { icon: Box, label: 'Box', onClick: () => this.cbs.onAddPrimitive('box') },
      { icon: Box, label: 'Sphere', onClick: () => this.cbs.onAddPrimitive('sphere') },
      { icon: Box, label: 'Cylinder', onClick: () => this.cbs.onAddPrimitive('cylinder') },
      { icon: Box, label: 'Capsule', onClick: () => this.cbs.onAddPrimitive('capsule') },
      { icon: Box, label: 'Plane', onClick: () => this.cbs.onAddPrimitive('plane') },
      { icon: Box, label: 'Circle', onClick: () => this.cbs.onAddPrimitive('circle') },
      { icon: Layers, label: 'Group', onClick: () => this.cbs.onAddPrimitive('group') },
      'sep',
      { icon: Lightbulb, label: 'Point Light', onClick: () => this.cbs.onAddLight('point') },
      { icon: Lightbulb, label: 'Directional Light', onClick: () => this.cbs.onAddLight('directional') },
      { icon: Lightbulb, label: 'Ambient Light', onClick: () => this.cbs.onAddLight('ambient') }
    ]));
    adders.appendChild(this.popoverButton(Upload, 'Import', [
      { icon: FilePlus2, label: 'Mesh (.glb)…', onClick: () => this.cbs.onImportMesh() },
      { icon: ImageIcon, label: 'HDR Environment…', onClick: () => this.cbs.onImportHdr() },
      'sep',
      { icon: Upload, label: 'Map ZIP…', onClick: () => this.cbs.onImportZip() }
    ]));
    this.root.appendChild(adders);

    // Snap
    const snapGroup = document.createElement('div');
    snapGroup.className = 'ed-toolbar-group';
    this.snapBtn = iconButton({ icon: Magnet, title: 'Snap to grid (toggle)', onClick: () => {
      this.tool.setSnap({ enabled: !this.tool.get().snap.enabled });
    }});
    snapGroup.appendChild(this.snapBtn);
    this.snapInput = document.createElement('input');
    this.snapInput.type = 'number';
    this.snapInput.className = 'ed-numeric';
    this.snapInput.step = '0.05';
    this.snapInput.style.width = '60px';
    this.snapInput.value = String(this.tool.get().snap.translate);
    this.snapInput.title = 'Translate snap unit';
    this.snapInput.onchange = () => {
      const v = parseFloat(this.snapInput.value);
      if (!Number.isNaN(v) && v > 0) this.tool.setSnap({ translate: v });
    };
    snapGroup.appendChild(this.snapInput);
    this.root.appendChild(snapGroup);

    // Axis lock + space
    const axisGroup = document.createElement('div');
    axisGroup.className = 'ed-toolbar-group';
    const mkAxis = (a: 'x' | 'y' | 'z') => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'ed-iconbtn compact';
      b.style.fontWeight = '700';
      b.style.fontSize = '12px';
      b.textContent = a.toUpperCase();
      b.title = `Toggle ${a.toUpperCase()} axis`;
      b.onclick = () => {
        const cur = this.tool.get().axisLock;
        this.tool.setAxisLock({ [a]: !cur[a] } as Partial<{ x: boolean; y: boolean; z: boolean }>);
      };
      this.axisBtns.set(a, b);
      axisGroup.appendChild(b);
    };
    mkAxis('x'); mkAxis('y'); mkAxis('z');
    this.spaceBtn = iconButton({ icon: Globe, title: 'World/Local space', label: 'World', onClick: () => {
      this.tool.setSpace(this.tool.get().space === 'world' ? 'local' : 'world');
    }});
    axisGroup.appendChild(this.spaceBtn);
    this.root.appendChild(axisGroup);

    // Spacer
    const spacer = document.createElement('div');
    spacer.className = 'ed-toolbar-spacer';
    this.root.appendChild(spacer);

    // Right side: playtest / export / exit
    const right = document.createElement('div');
    right.className = 'ed-toolbar-group';
    const exportBtn = iconButton({
      icon: Download, title: 'Export ZIP', label: 'Export',
      onClick: () => this.cbs.onExport()
    });
    right.appendChild(exportBtn);
    this.playtestBtn = iconButton({
      icon: Play, title: 'Playtest', label: 'Playtest',
      onClick: () => this.cbs.onPlaytest(),
      className: 'primary'
    });
    right.appendChild(this.playtestBtn);
    const exitBtn = iconButton({ icon: Cog, title: 'Exit to home', label: 'Exit', onClick: () => this.cbs.onExit() });
    right.appendChild(exitBtn);
    this.root.appendChild(right);
  }

  private popoverButton(icon: IconNode, label: string, items: Array<'sep' | { icon: IconNode; label: string; onClick: () => void }>): HTMLElement {
    const wrap = document.createElement('div');
    wrap.style.position = 'relative';
    // Build the button content directly so the chevron sits inline with the
    // icon + label without an innerHTML round-trip.
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ed-iconbtn';
    btn.title = label;
    btn.innerHTML =
      renderIcon(icon, 16) +
      `<span class="ed-iconbtn-label">${label}</span>` +
      `<span class="ed-icon" style="margin-left:2px">${renderIcon(ChevronDown, 12)}</span>`;
    wrap.appendChild(btn);
    let popover: HTMLElement | null = null;
    const close = (): void => {
      popover?.remove();
      popover = null;
      removeEventListener('mousedown', onDocMouse, true);
    };
    const onDocMouse = (e: MouseEvent): void => {
      if (popover && !popover.contains(e.target as Node) && !btn.contains(e.target as Node)) close();
    };
    btn.onclick = (): void => {
      if (popover) { close(); return; }
      popover = document.createElement('div');
      popover.className = 'ed-popover';
      const rect = btn.getBoundingClientRect();
      popover.style.left = `${rect.left}px`;
      popover.style.top = `${rect.bottom + 4}px`;
      popover.style.position = 'fixed';
      for (const it of items) {
        if (it === 'sep') {
          const sep = document.createElement('div');
          sep.className = 'ed-popover-sep';
          popover.appendChild(sep);
          continue;
        }
        const ib = document.createElement('button');
        ib.type = 'button';
        ib.className = 'ed-popover-item';
        ib.innerHTML = `<span class="ed-icon">${renderIcon(it.icon, 14)}</span><span>${it.label}</span>`;
        ib.onclick = () => { it.onClick(); close(); };
        popover.appendChild(ib);
      }
      // Append inside the editor root so the scoped CSS variables and
      // `.ed-popover` styles apply. `position: fixed` keeps it positioned
      // relative to the viewport regardless of ancestor overflow.
      const editorRoot = document.getElementById('map-editor') ?? document.body;
      editorRoot.appendChild(popover);
      setTimeout(() => addEventListener('mousedown', onDocMouse, true), 0);
    };
    return wrap;
  }

  private applyToolState(): void {
    const t = this.tool.get();
    for (const [m, b] of this.toolButtons) {
      b.classList.toggle('is-active', m === t.mode);
    }
    this.snapBtn.classList.toggle('is-active', t.snap.enabled);
    this.spaceBtn.querySelector('.ed-iconbtn-label')!.textContent = t.space === 'world' ? 'World' : 'Local';
    for (const [a, b] of this.axisBtns) {
      b.classList.toggle('is-active', !!t.axisLock[a]);
    }
  }

  private applyDocState(): void {
    const s = this.state.get();
    if (this.mapSelect.value !== s.bundleId) this.mapSelect.value = s.bundleId;
  }

  setPlaytestEnabled(enabled: boolean): void {
    this.playtestBtn.disabled = !enabled;
  }
}
